import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import useApi from "../../customhook/useApi";
import {
  SearchFieldName,
  SearchFilterProps,
  SearchFilterRef,
  SearchPayload,
} from "./types";
import MultiSearchableSelect, { Option } from "../MultiSearchableSelect";

const initialPayload: SearchPayload = {
  org_ids: [],
  project_ids: [],
  location_name: [],
  program_ids: [],
  department_ids: [],
};

const SearchFilter = forwardRef<SearchFilterRef, SearchFilterProps>(
  ({ value, onChange, storageKey, errors }, ref) => {
    // Root element ref, used by focusField to find the correct dropdown
    const rootRef = useRef<HTMLDivElement | null>(null);

    const [organizationOptions, setOrganizationOptions] = useState<Option[]>(
      [],
    );
    const [projectOptions, setProjectOptions] = useState<Option[]>([]);
    const [locationOptions, setLocationOptions] = useState<Option[]>([]);
    const [programOptions, setProgramOptions] = useState<Option[]>([]);
    const [departmentOptions, setDepartmentOptions] = useState<Option[]>([]);

    const getOrganizationAPI = useApi<any>(
      "/api/v1/agency/organizations/manager",
      {
        method: "GET",
        autoCall: false,
        handleGlobalError: false,
      },
    );

    const getProjectAPI = useApi<any>("/api/v1/agency/accounts/manager", {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    });

    const getLocationAPI = useApi<any>("/api/v1/agency/locations/manager", {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    });

    const getProgramAPI = useApi<any>("/api/v1/agency/programs/manager", {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    });

    const getDepartmentAPI = useApi<any>("/api/v1/agency/departments/manager", {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    });

    const saveToStorage = (data: SearchPayload) => {
      try {
        localStorage.setItem(storageKey, JSON.stringify(data));
      } catch (error) {
        console.error("Failed to save filter storage:", error);
      }
    };

    const getStorage = (): SearchPayload | null => {
      try {
        const data = localStorage.getItem(storageKey);

        if (!data) {
          return null;
        }

        const parsed = JSON.parse(data);

        return {
          org_ids: Array.isArray(parsed?.org_ids)
            ? parsed.org_ids.map(String)
            : parsed?.org_ids
              ? [String(parsed.org_ids)]
              : [],

          project_ids: Array.isArray(parsed?.project_ids)
            ? parsed.project_ids.map(String)
            : parsed?.project_ids
              ? [String(parsed.project_ids)]
              : [],

          location_name: Array.isArray(parsed?.location_name)
            ? parsed.location_name.map(String)
            : parsed?.location_name
              ? [String(parsed.location_name)]
              : [],

          program_ids: Array.isArray(parsed?.program_ids)
            ? parsed.program_ids.map(String)
            : parsed?.program_ids
              ? [String(parsed.program_ids)]
              : [],

          department_ids: Array.isArray(parsed?.department_ids)
            ? parsed.department_ids.map(String)
            : parsed?.department_ids
              ? [String(parsed.department_ids)]
              : [],
        };
      } catch (error) {
        console.error("Failed to read filter storage:", error);
        return null;
      }
    };

    const clearStorage = () => {
      localStorage.removeItem(storageKey);
    };

    const updateFilters = (data: SearchPayload) => {
      onChange(data);
      saveToStorage(data);
    };

    const resetProjects = () => {
      setProjectOptions([]);
    };

    const resetLocations = () => {
      setLocationOptions([]);
    };

    const resetPrograms = () => {
      setProgramOptions([]);
    };

    const resetDepartments = () => {
      setDepartmentOptions([]);
    };

    const resetChildDropdowns = (
      level: "organization" | "project" | "location" | "program",
    ) => {
      switch (level) {
        case "organization":
          resetProjects();
          resetLocations();
          resetPrograms();
          resetDepartments();
          break;

        case "project":
          resetLocations();
          resetPrograms();
          resetDepartments();
          break;

        case "location":
          resetPrograms();
          resetDepartments();
          break;

        case "program":
          resetDepartments();
          break;

        default:
          break;
      }
    };

    const fetchOrganizations = async () => {
      try {
        const res = await getOrganizationAPI.callApi();

        if (!res?.success) {
          setOrganizationOptions([]);
          return [];
        }

        const options: Option[] =
          res.data?.map((item: any) => ({
            value: String(item.org_id),
            label: item.name,
          })) || [];

        setOrganizationOptions(options);

        return options;
      } catch (error) {
        console.error("Error fetching organizations:", error);
        setOrganizationOptions([]);
        return [];
      }
    };

    const fetchProjects = async (orgIds: string[]) => {
      try {
        if (!orgIds.length) {
          setProjectOptions([]);
          return;
        }

        const res = await getProjectAPI.callApi({
          orgIds,
        });

        if (!res?.success) {
          setProjectOptions([]);
          return;
        }

        const options: Option[] =
          (res.data || []).map((item: any) => ({
            value: String(item.project_id),
            label: item.project_name,
          })) || [];

        setProjectOptions(options);
      } catch (error) {
        console.error("Error fetching projects:", error);
        setProjectOptions([]);
      }
    };

    const fetchLocations = async (projectIds: string[]) => {
      try {
        if (!projectIds.length) {
          setLocationOptions([]);
          return;
        }

        const res = await getLocationAPI.callApi({
          projectIds,
        });

        if (!res?.success) {
          setLocationOptions([]);
          return;
        }

        const options: Option[] =
          (res.data || []).map((item: any) => ({
            value: item.location_name,
            label: item.location_name,
          })) || [];

        setLocationOptions(options);
      } catch (error) {
        console.error("Error fetching locations:", error);
        setLocationOptions([]);
      }
    };

    const fetchPrograms = async (
      projectIds: string[],
      locationNames: string[],
    ) => {
      try {
        if (!projectIds.length || !locationNames.length) {
          setProgramOptions([]);
          return;
        }

        const res = await getProgramAPI.callApi({
          projectIds,
          locationNames,
        });

        if (!res?.success) {
          setProgramOptions([]);
          return;
        }

        const options: Option[] =
          (res.data || []).map((item: any) => ({
            value: String(item.program_id),
            label: item.program_name,
          })) || [];

        setProgramOptions(options);
      } catch (error) {
        console.error("Error fetching programs:", error);
        setProgramOptions([]);
      }
    };

    const fetchDepartments = async (programIds: string[]) => {
      try {
        if (!programIds.length) {
          setDepartmentOptions([]);
          return;
        }

        const res = await getDepartmentAPI.callApi({
          programIds,
        });

        if (!res?.success) {
          setDepartmentOptions([]);
          return;
        }

        const options: Option[] =
          (res.data || []).map((item: any) => ({
            value: String(item.department_id),
            label: item.department_name,
          })) || [];

        setDepartmentOptions(options);
      } catch (error) {
        console.error("Error fetching departments:", error);
        setDepartmentOptions([]);
      }
    };

    const handleSelectChange = async (
      name: string,
      selectedValues: string[],
      _selectedOptions: Option[],
    ) => {
      let updated: SearchPayload = { ...value };

      switch (name) {
        // =====================================================
        // Organization
        // =====================================================

        case "org_id":
          updated = {
            org_ids: selectedValues,
            project_ids: [],
            location_name: [],
            program_ids: [],
            department_ids: [],
          };

          resetChildDropdowns("organization");

          updateFilters(updated);

          if (selectedValues.length > 0) {
            await fetchProjects(selectedValues);
          }

          break;

        // =====================================================
        // Account / Project
        // =====================================================

        case "project_id":
          updated = {
            ...value,
            project_ids: selectedValues,
            location_name: [],
            program_ids: [],
            department_ids: [],
          };

          resetChildDropdowns("project");

          updateFilters(updated);

          if (selectedValues.length > 0) {
            await fetchLocations(selectedValues);
          }

          break;

        // =====================================================
        // Location
        // =====================================================

        case "location_name":
          updated = {
            ...value,
            location_name: selectedValues,
            program_ids: [],
            department_ids: [],
          };

          resetChildDropdowns("location");

          updateFilters(updated);

          if (selectedValues.length > 0 && updated.project_ids.length > 0) {
            await fetchPrograms(updated.project_ids, selectedValues);
          }

          break;

        // =====================================================
        // Program
        // =====================================================

        case "program_id":
          updated = {
            ...value,
            program_ids: selectedValues,
            department_ids: [],
          };

          resetChildDropdowns("program");

          updateFilters(updated);

          if (selectedValues.length > 0) {
            await fetchDepartments(selectedValues);
          }

          break;

        // =====================================================
        // Department
        // =====================================================

        case "department_id":
          updated = {
            ...value,
            department_ids: selectedValues,
          };

          updateFilters(updated);

          break;

        default:
          break;
      }
    };

    const initialize = async () => {
      try {
        await fetchOrganizations();
        const stored = getStorage();

        if (!stored) {
          return;
        }

        onChange(stored);
        if (stored.org_ids.length > 0) {
          await fetchProjects(stored.org_ids);
        }
        if (stored.project_ids.length > 0) {
          await fetchLocations(stored.project_ids);
        }
        if (stored.project_ids.length > 0 && stored.location_name.length > 0) {
          await fetchPrograms(stored.project_ids, stored.location_name);
        }
        if (stored.program_ids.length > 0) {
          await fetchDepartments(stored.program_ids);
        }
      } catch (error) {
        console.error("Error initializing search filters:", error);
      }
    };

    useEffect(() => {
      initialize();
    }, []);

    const clearFilters = async () => {
      clearStorage();

      resetProjects();
      resetLocations();
      resetPrograms();
      resetDepartments();

      updateFilters(initialPayload);

      await fetchOrganizations();
    };

    /** Focus + scroll to a dropdown trigger. Called by the parent (Search click only). */
    const focusField = (name: SearchFieldName) => {
      const trigger = rootRef.current?.querySelector<HTMLElement>(
        `[data-field-name="${name}"] .msearch-trigger`,
      );
      if (!trigger) return;
      trigger.focus({ preventScroll: true });
      trigger.scrollIntoView({ behavior: "smooth", block: "center" });
    };

    useImperativeHandle(ref, () => ({
      clear: clearFilters,
      focusField,
    }));

    return (
      <div className="row" ref={rootRef}>
        <div className="col-6 col-lg-4">
          <MultiSearchableSelect
            label="Organization"
            name="org_id"
            required
            error={errors?.org_ids}
            value={value.org_ids}
            options={organizationOptions}
            loading={getOrganizationAPI.loading}
            onChange={handleSelectChange}
          />
        </div>
        <div className="col-6 col-lg-4 mb-3">
          <MultiSearchableSelect
            label="Account"
            name="project_id"
            required
            error={errors?.project_ids}
            value={value.project_ids}
            options={projectOptions}
            loading={getProjectAPI.loading}
            disabled={value.org_ids.length === 0}
            onChange={handleSelectChange}
          />
        </div>
        <div className="col-6 col-lg-4 mb-3">
          <MultiSearchableSelect
            label="Location"
            name="location_name"
            value={value.location_name}
            options={locationOptions}
            loading={getLocationAPI.loading}
            disabled={value.project_ids.length === 0}
            onChange={handleSelectChange}
          />
        </div>
        <div className="col-6 col-lg-4 mb-3">
          <MultiSearchableSelect
            label="Program"
            name="program_id"
            value={value.program_ids}
            options={programOptions}
            loading={getProgramAPI.loading}
            disabled={value.location_name.length === 0}
            onChange={handleSelectChange}
          />
        </div>
        <div className="col-6 col-lg-4 mb-3">
          <MultiSearchableSelect
            label="Department"
            name="department_id"
            value={value.department_ids}
            options={departmentOptions}
            loading={getDepartmentAPI.loading}
            disabled={value.program_ids.length === 0}
            onChange={handleSelectChange}
          />
        </div>
      </div>
    );
  },
);

SearchFilter.displayName = "SearchFilter";

export default SearchFilter; 